import { Group, Stack, Text, useMantineTheme } from '@mantine/core';
import type { MRT_Cell } from 'mantine-react-table';

import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ModificationTag } from '../../../../../../shared/components/UI/MidificationTag/ModificationTag';
import { RenewalTag } from '../../../../../../shared/components/UI/RenewalTag/RenewalTag';
import TenderStatusBadge from '../../../../../../shared/components/UI/TenderStatusBadge/TenderStatusBadge';
import { Tooltip } from '../../../../../../shared/components/UI/Tooltip/Tooltip';

import type { TenderWithListData } from '../../../../../../shared/api/magellan/interaction/dto';
import type { Rectification } from '../../../../../../shared/entities/Rectification';
import type { TenderStatus } from '../../../../../../shared/entities/Tender';
import { formatDateInParisTZ } from '../../../../../../shared/utils/dates';

export type StatusCellData = {
  status: TenderStatus;
  isRenewal: boolean;
  rectifications: Rectification[];
};

type StatusCellProps = {
  cell: MRT_Cell<TenderWithListData>;
};

export function StatusCell({ cell }: StatusCellProps) {
  const { t } = useTranslation(['pipeline']);
  const { status, isRenewal, rectifications } = cell.getValue<StatusCellData>();
  return (
    <Group noWrap spacing="01">
      <TenderStatusBadge status={status} />
      {isRenewal && <RenewalTag size="xs" />}
      {rectifications.length > 0 && (
        <Tooltip
          width="220px"
          content={<TooltipContent rectifications={rectifications} />}
        >
          <ModificationTag content={t('cells.status.modified')} />
        </Tooltip>
      )}
    </Group>
  );
}

const TooltipContent = ({
  rectifications,
}: {
  rectifications: Rectification[];
}) => {
  const { t } = useTranslation(['pipeline', 'domain']);
  const theme = useMantineTheme();
  const rectificationDate = rectifications[0].publishedAt;

  return (
    <Stack spacing="01">
      <Text variant="xs" fw="400">
        {t('cells.status.tooltip.title')}
      </Text>
      {rectifications.map(rectification => (
        <Text key={rectification.boampId} variant="xs" fw="400" color="gray.6">
          <Trans
            ns="notice"
            i18nKey="documents.timeLineLabels.rectifications"
            components={{
              rectificationLink: (
                <Link
                  to={`https://www.boamp.fr/pages/avis/?q=idweb:"${rectification.boampId}"`}
                  target="_blank"
                  style={{
                    color: theme.colors.primary[7],
                    textDecoration: 'underline',
                  }}
                >
                  {t('domain:rectification')}
                </Link>
              ),
            }}
            context={rectificationDate ? 'withdate' : undefined}
            values={{
              date: rectificationDate
                ? formatDateInParisTZ(rectificationDate)
                : undefined,
            }}
          />
        </Text>
      ))}
    </Stack>
  );
};
